<template>
	<div
		ref="tooltipElement"
		class="relative inline-block cursor-pointer"
		@mouseenter="show"
		@mouseleave="hide">
		<slot></slot>

		<Teleport to="body">
			<div
				v-if="isVisible"
				ref="tooltip"
				:class="classes"
				:style="{ top: top + 'px', left: left + 'px' }">
				{{ text }}
				<div :class="arrowClasses"></div>
			</div>
		</Teleport>
	</div>
</template>

<script lang="ts" setup>
	import { ref, computed, nextTick } from 'vue'

	const tooltipElement = ref<HTMLElement | null>(null)
	const tooltip = ref<HTMLElement | null>(null)
	const isVisible = ref(false)
	const opacity = ref(false)
	const top = ref(0)
	const left = ref(0)

	const props = withDefaults(
		defineProps<{
			text: string
			theme?: 'dark' | 'light'
			small?: boolean
			positionY?: 'top' | 'bottom'
			positionX?: 'left' | 'right' | 'center'
		}>(),
		{
			theme: 'dark',
			positionY: 'top',
			positionX: 'center',
			small: false
		}
	)

	const classes = computed(() => ({
		'z-[9999] fixed transition-opacity duration-200 rounded shadow-md pointer-events-none':
			true,
		'bg-gray-900 text-white': props.theme == 'dark',
		'bg-white text-mabey-950': props.theme == 'light',
		'px-3 py-2 text-xs': !props.small,
		'px-2 py-1 text-xs': props.small,
		'opacity-0': !opacity.value,
		'opacity-100': opacity.value
	}))

	const arrowClasses = computed(() => ({
		'absolute w-0 h-0 border-solid border-transparent border-[6px] left-1/2 -translate-x-1/2':
			true,
		'top-[100%] border-t-gray-900': props.positionY === 'top' && props.theme === 'dark',
		'bottom-[100%] border-b-gray-900': props.positionY === 'bottom' && props.theme === 'dark',
		'top-[100%] border-t-white': props.positionY === 'top' && props.theme === 'light',
		'bottom-[100%] border-b-white': props.positionY === 'bottom' && props.theme === 'light'
	}))

	const updatePosition = () => {
		if (!tooltipElement.value || !tooltip.value) return

		const rect = tooltipElement.value.getBoundingClientRect()
		const tooltipRect = tooltip.value.getBoundingClientRect()

		let t = 0
		let l = 0

		if (props.positionY === 'top') {
			t = rect.top - tooltipRect.height - 8 // 8px offset
		} else {
			t = rect.bottom + 8
		}

		if (props.positionX === 'center') {
			l = rect.left + rect.width / 2 - tooltipRect.width / 2
		} else if (props.positionX === 'left') {
			l = rect.left
		} else {
			l = rect.right - tooltipRect.width
		}

		top.value = t
		left.value = l
	}

	const show = async () => {
		isVisible.value = true
		await nextTick()
		updatePosition()
		requestAnimationFrame(() => {
			opacity.value = true
		})
	}

	const hide = () => {
		opacity.value = false
		setTimeout(() => {
			isVisible.value = false
		}, 200)
	}
</script>
